/**
 * Copyright Highway9 Networks Inc.
 *
 * Client for the prompt library API at /api/v1/saas/ai-assistant/prompts.
 */

import type {
  AssistantPrompt,
  AssistantPromptVersion,
} from "@h9/ui-lib/components/organisms/AiAssistant";
import { authenticationService } from "./authentication-service";

const REST_BASE = "/api/v1/saas/ai-assistant/prompts";

export type { AssistantPrompt, AssistantPromptVersion };

const authHeader = () => ({
  Authorization: `Bearer ${authenticationService?.currentUserValue?.token ?? ""}`,
});

export const zivaPromptService = {
  async list(): Promise<AssistantPrompt[]> {
    const r = await fetch(REST_BASE, { headers: { ...authHeader() } });
    if (!r.ok) {
      throw new Error(`Failed to load prompt library (${r.status})`);
    }
    const text = await r.text();
    if (!text.trim()) return [];
    try {
      const body = JSON.parse(text) as { data?: AssistantPrompt[] };
      return body?.data ?? [];
    } catch {
      return [];
    }
  },

  async get(id: string): Promise<AssistantPrompt> {
    const r = await fetch(`${REST_BASE}/${encodeURIComponent(id)}`, {
      headers: { ...authHeader() },
    });
    if (!r.ok) {
      throw new Error(await extractError(r, "load prompt"));
    }
    return (await r.json()) as AssistantPrompt;
  },

  async create(prompt: AssistantPrompt): Promise<AssistantPrompt> {
    const r = await fetch(REST_BASE, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify(prompt),
    });
    if (!r.ok) {
      throw new Error(await extractError(r, "create prompt"));
    }
    return (await r.json()) as AssistantPrompt;
  },

  async update(id: string, prompt: AssistantPrompt): Promise<AssistantPrompt> {
    const r = await fetch(`${REST_BASE}/${encodeURIComponent(id)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify(prompt),
    });
    if (!r.ok) {
      throw new Error(await extractError(r, "update prompt"));
    }
    return (await r.json()) as AssistantPrompt;
  },

  async remove(id: string): Promise<void> {
    const r = await fetch(`${REST_BASE}/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { ...authHeader() },
    });
    if (!r.ok) {
      throw new Error(await extractError(r, "delete prompt"));
    }
  },

  async removeVersion(promptId: string, versionId: string): Promise<void> {
    const r = await fetch(
      `${REST_BASE}/${encodeURIComponent(promptId)}/versions/${encodeURIComponent(versionId)}`,
      {
        method: "DELETE",
        headers: { ...authHeader() },
      }
    );
    if (!r.ok) {
      throw new Error(await extractError(r, "delete prompt version"));
    }
  },
};

async function extractError(r: Response, action: string): Promise<string> {
  try {
    const body = (await r.json()) as { errors?: { message?: string }[] };
    const msg = body?.errors?.[0]?.message;
    if (msg) return msg;
  } catch {
    // ignore parse errors
  }
  return `Failed to ${action} (${r.status})`;
}
